import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import * as Haptics from 'expo-haptics';
import { Svg, Path } from 'react-native-svg';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { apiService } from '@/services/apiService';
import BaseModal from '@/components/BaseModal';
import TertiaryButton from '@/components/TertiaryButton';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import RevenueCatService from '@/services/revenueCatService';

interface EloStep {
  step_number: number;
  title: string;
  subtitle: string;
  is_unlocked: boolean;
  is_completed: boolean;
  scroll_percentage: number;
  audio_url?: string;
  is_plus?: boolean;
}

interface EloTrailDetail {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  step_count: number;
  cover_image_url: string | null;
  steps: EloStep[];
  user_progress: {
    completedCount: number;
    totalSteps: number;
    progressPercent: number;
  };
}

/* ── Icons ─────────────────────────────────────────────── */

const BackArrowIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path d="M15.75 19L8.75 12L15.75 5" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
  </Svg>
);

const InfoIcon = () => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M2.5 10C2.5 14.1416 5.85757 17.5 10 17.5C14.1424 17.5 17.5 14.1416 17.5 10C17.5 5.85757 14.1424 2.5 10 2.5C5.85757 2.5 2.5 5.85757 2.5 10Z"
      stroke="#151515"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M10.0048 13.0777V9.49466M10 6.96188V6.90918"
      stroke="#151515"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ChevronRight16 = ({ color = '#151515' }: { color?: string }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke={color} strokeWidth="2" strokeLinecap="square" />
  </Svg>
);

const CheckmarkIcon = () => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M4.16699 10.4167L7.50033 13.75L15.8337 5.41675"
      stroke="#0fa971"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const FlagIconActive = () => (
  <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <Path
      d="M8.7314 20.9046L5.51377 5.12595L16.4675 3L15.0683 7.60703L18.0709 11.2654L7.26318 13.5509M4.09375 21H19.9045"
      stroke="#015480"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const FlagIconGhost = () => (
  <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <Path
      d="M8.7314 20.9046L5.51377 5.12595L16.4675 3L15.0683 7.60703L18.0709 11.2654L7.26318 13.5509M4.09375 21H19.9045"
      stroke="#BDC2C8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const LockIcon = () => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M14.1667 8.33333V6.66667C14.1667 4.36667 12.3 2.5 10 2.5C7.7 2.5 5.83333 4.36667 5.83333 6.66667V8.33333"
      stroke="#BDC2C8"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.333 17.5H6.66634C5.28967 17.5 4.16634 16.3767 4.16634 15V10.8333C4.16634 9.45667 5.28967 8.33333 6.66634 8.33333H13.333C14.7097 8.33333 15.833 9.45667 15.833 10.8333V15C15.833 16.3767 14.7097 17.5 13.333 17.5Z"
      stroke="#BDC2C8"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M10 12.0833V13.75" stroke="#BDC2C8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const GemIconStep = () => (
  <Svg width="14" height="14" viewBox="0 0 12 12" fill="none">
    <Path
      d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M1.50391 5.10156H10.4954" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

/* ── Component ─────────────────────────────────────────── */

export default function TrailDetailScreen() {
  const { t } = useLanguage();
  const router = useRouter();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { session, sessionReady, user } = useAuth();
  const { hasPadrinhoPlus } = useServerVerifiedPlus();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [trail, setTrail] = useState<EloTrailDetail | null>(null);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  useFocusEffect(
    useCallback(() => {
      // PV2-16 (CL-P03): session?.access_token removed from deps — token rotation
      // must not trigger a data refetch. Only user identity and slug changes matter.
      if (user && sessionReady && slug) {
        loadTrailDetail();
      }
    }, [user, sessionReady, slug])
  );

  const loadTrailDetail = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/api/trails/${slug}`);
      if (response.success && response.data?.trail) {
        setTrail(response.data.trail);
        // PRD §5.8 — `trail_section_opened`. Fired only after a successful
        // load so we don't double-count failed retries; `section_id` is
        // the slug (the only stable identifier this screen has).
        import('@/utils/clarityEvents')
          .then(({ logTrailSectionOpened }) =>
            logTrailSectionOpened({ section_id: String(slug) })
          )
          .catch(() => {});
      }
    } catch (error) {
      console.error('Error loading trail detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    try {
      const response = await apiService.get(`/api/trails/${slug}`);
      if (response.success && response.data?.trail) {
        setTrail(response.data.trail);
      }
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      console.error('Error refreshing trail detail:', error);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } finally {
      setRefreshing(false);
    }
  }, [slug]);

  const handleStepPress = async (step: EloStep) => {
    const isPlusStep = !!step.is_plus;
    const needsUpgrade = isPlusStep && !hasPadrinhoPlus;

    if (needsUpgrade) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      try {
        await RevenueCatService.presentPaywall({ fromSurface: 'trail_step' });
      } catch (e) {
        console.error('Paywall error', e);
      }
      return;
    }

    if (!step.is_unlocked) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
      return;
    }
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/trail/${slug}/${step.step_number}?trailName=${encodeURIComponent(trail?.name || '')}` as any);
  };

  const handleResetProgress = async () => {
    if (!trail) return;
    try {
      setResetting(true);
      await apiService.delete('/api/trail/progress', {
        body: JSON.stringify({ trail_slug: slug }),
        headers: { 'Content-Type': 'application/json' },
      });
      setShowResetModal(false);
      await loadTrailDetail();
    } catch (error) {
      console.error('Error resetting progress:', error);
    } finally {
      setResetting(false);
    }
  };

  const pct = trail?.user_progress?.progressPercent ?? 0;
  const completedCount = trail?.user_progress?.completedCount ?? 0;
  const totalSteps = trail?.user_progress?.totalSteps ?? 0;

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* ── Header ── */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.headerBtn}>
          <BackArrowIcon />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{trail?.name || t('trail.title')}</Text>
        <View style={styles.headerBtn} />
      </View>

      {/* ── Content ── */}
      {loading ? (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.topSection}>
            <View style={styles.skeletonTitleBlock} />
            <View style={styles.skeletonDescLine1} />
            <View style={styles.skeletonDescLine2} />
            <View style={styles.skeletonProgressSection}>
              <View style={styles.skeletonProgressLabelRow}>
                <View style={styles.skeletonProgressLabel} />
                <View style={styles.skeletonProgressCount} />
              </View>
              <View style={styles.skeletonProgressTrack} />
            </View>
            <View style={styles.skeletonIntroRow}>
              <View style={styles.skeletonIntroIcon} />
              <View style={styles.skeletonIntroText}>
                <View style={styles.skeletonIntroLine1} />
                <View style={styles.skeletonIntroLine2} />
              </View>
              <View style={styles.skeletonChevron} />
            </View>
          </View>
          <View style={styles.stepsSection}>
            {[0, 1, 2, 3].map(i => (
              <React.Fragment key={i}>
                <View style={styles.stepRow}>
                  <View style={styles.skeletonStepIcon} />
                  <View style={styles.stepInfo}>
                    <View style={styles.skeletonStepLine1} />
                    <View style={styles.skeletonStepLine2} />
                  </View>
                  <View style={styles.skeletonChevron} />
                </View>
              </React.Fragment>
            ))}
          </View>
        </ScrollView>
      ) : trail ? (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor="#669AB7"
              colors={['#669AB7']}
              progressViewOffset={0}
            />
          }
        >
          {/* ── Top section (Spring Wood bg) ── */}
          <View style={styles.topSection}>
            <Text style={styles.trailName}>{trail.name}</Text>

            {trail.description ? (
              <Text style={styles.trailDescription}>{trail.description}</Text>
            ) : null}

            {/* Progress */}
            <View style={styles.progressSection}>
              <View style={styles.progressLabelRow}>
                <Text style={styles.progressLabel}>
                  {pct}% {t('trail.progressLabel')}
                </Text>
                <Text style={styles.progressCount}>
                  {completedCount}/{totalSteps}
                </Text>
              </View>
              <View style={styles.progressTrack}>
                <View style={[styles.progressFill, { width: `${pct}%` as any }]} />
              </View>
            </View>

            {/* Intro row */}
            <TouchableOpacity
              style={styles.milestoneRow}
              onPress={() => setShowIntroModal(true)}
              activeOpacity={0.7}
            >
              <View style={styles.iconWestar}>
                <InfoIcon />
              </View>
              <View style={styles.milestoneInfo}>
                <Text style={styles.milestoneTitle}>{t('trail.introTitle')}</Text>
                <Text style={styles.milestoneSubtitle}>{t('trail.introSubtitle')}</Text>
              </View>
              <ChevronRight16 />
            </TouchableOpacity>
          </View>

          {/* ── Steps section (white bg) ── */}
          <View style={styles.stepsSection}>
            {trail.steps.map((step, idx) => {
              const isPlusStep = !!step.is_plus;
              const needsUpgrade = isPlusStep && !hasPadrinhoPlus;
              return (
                <React.Fragment key={step.step_number}>
                  <TouchableOpacity
                    style={styles.stepRow}
                    onPress={() => handleStepPress(step)}
                    activeOpacity={step.is_unlocked || needsUpgrade ? 0.7 : 1}
                  >
                    {/* Step icon square */}
                    <View style={[
                      styles.stepIcon,
                      step.is_completed && styles.stepIconCompleted,
                      !step.is_completed && step.is_unlocked && !needsUpgrade && styles.stepIconCurrent,
                      (!step.is_unlocked || needsUpgrade) && !step.is_completed && styles.stepIconLocked,
                    ]}>
                      {step.is_completed ? (
                        <CheckmarkIcon />
                      ) : step.is_unlocked && !needsUpgrade ? (
                        <FlagIconActive />
                      ) : (
                        <FlagIconGhost />
                      )}
                    </View>

                    {/* Step info */}
                    <View style={styles.stepInfo}>
                      <Text style={styles.stepTitle} numberOfLines={2}>{step.title}</Text>
                      <Text style={styles.stepSubtitle}>
                        {`${t('trail.step')} ${step.step_number}`}
                      </Text>
                    </View>

                    {/* Right action */}
                    {needsUpgrade ? (
                      <View style={styles.plusBadge}>
                        <GemIconStep />
                        <Text style={styles.plusBadgeText}>Plus</Text>
                      </View>
                    ) : step.is_unlocked ? (
                      <ChevronRight16 />
                    ) : (
                      <View style={styles.lockedAction}>
                        <Text style={styles.lockedText}>{t('trail.lockButtonText')}</Text>
                        <LockIcon />
                      </View>
                    )}
                  </TouchableOpacity>
                </React.Fragment>
              );
            })}
          </View>

          {/* ── Reset ── */}
          {completedCount > 0 && (
            <View style={styles.resetSection}>
              <TertiaryButton
                title={t('trail.reset')}
                onPress={() => setShowResetModal(true)}
              />
            </View>
          )}
        </ScrollView>
      ) : (
        <View style={styles.loadingContainer}>
          <Text style={styles.emptyText}>Trilha não encontrada</Text>
        </View>
      )}

      {/* Intro Modal */}
      <BaseModal
        visible={showIntroModal}
        onClose={() => setShowIntroModal(false)}
        title={t('trail.introModalTitle')}
        message={t('trail.introModalMessage')}
        primaryButtonText={t('trail.introButton')}
        onPrimaryPress={() => setShowIntroModal(false)}
      />

      {/* Reset Modal */}
      <BaseModal
        visible={showResetModal}
        onClose={() => setShowResetModal(false)}
        title={t('trail.resetTitle')}
        message={t('trail.resetMessage')}
        primaryButtonText={resetting ? t('trail.resetting') : t('trail.resetButton')}
        onPrimaryPress={handleResetProgress}
        secondaryButtonText={t('trail.cancelButton')}
        onSecondaryPress={() => setShowResetModal(false)}
      />
    </SafeAreaView>
  );
}

/* ── Styles ─────────────────────────────────────────────── */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },

  /* Header */
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    height: 56,
    backgroundColor: '#F9F8F3',
  },
  headerBtn: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#151515',
    lineHeight: 14,
    flex: 1,
    textAlign: 'center',
    paddingHorizontal: 8,
  },

  /* Scroll */
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 48,
  },

  /* Loading / empty */
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#7E7D75',
  },

  /* ── Top section (Spring Wood) ── */
  topSection: {
    backgroundColor: '#F9F8F3',
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 24,
  },
  trailName: {
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    color: '#151515',
    lineHeight: 28.8,
  },
  trailDescription: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#151515',
    lineHeight: 19.6,
  },

  /* Progress */
  progressSection: {
    gap: 8,
  },
  progressLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progressLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: '#669AB7',
    lineHeight: 19.6,
  },
  progressCount: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: '#151515',
    lineHeight: 19.6,
  },
  progressTrack: {
    width: '100%',
    height: 4,
    backgroundColor: '#F0EEE4',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: 4,
    backgroundColor: '#669AB7',
    borderRadius: 2,
  },

  /* Intro (milestone row) */
  milestoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  iconWestar: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#DAD8CF',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    padding: 10,
  },
  milestoneInfo: {
    flex: 1,
    gap: 4,
  },
  milestoneTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#151515',
    lineHeight: 14,
  },
  milestoneSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#757C85',
    lineHeight: 14,
  },

  /* ── Steps section (white) ── */
  stepsSection: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 24,
  },

  /* Step row */
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  stepIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    padding: 10,
  },
  stepIconCompleted: {
    backgroundColor: '#CFEEE3',
  },
  stepIconCurrent: {
    backgroundColor: '#E6EBF0',
  },
  stepIconLocked: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#DAD8CF',
    backgroundColor: 'transparent',
  },
  stepInfo: {
    flex: 1,
    gap: 4,
  },
  stepTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#151515',
    lineHeight: 14,
  },
  stepSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#757C85',
    lineHeight: 14,
  },
  lockedAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  lockedText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: '#BDC2C8',
    lineHeight: 19.6,
  },
  plusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#669AB7',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 5,
    flexShrink: 0,
  },
  plusBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: '#FFFFFF',
    lineHeight: 13,
  },

  /* Reset */
  resetSection: {
    marginHorizontal: 24,
    marginTop: 24,
    alignItems: 'center',
  },

  /* ── Skeletons ── */
  skeletonTitleBlock: {
    width: '60%',
    height: 28,
    backgroundColor: '#E7E6E0',
    borderRadius: 8,
  },
  skeletonDescLine1: {
    width: '100%',
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonDescLine2: {
    width: '75%',
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonProgressSection: {
    gap: 8,
  },
  skeletonProgressLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  skeletonProgressLabel: {
    width: 100,
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonProgressCount: {
    width: 40,
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonProgressTrack: {
    width: '100%',
    height: 4,
    backgroundColor: '#E7E6E0',
    borderRadius: 2,
  },
  skeletonIntroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  skeletonIntroIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#E7E6E0',
  },
  skeletonIntroText: {
    flex: 1,
    gap: 6,
  },
  skeletonIntroLine1: {
    width: '50%',
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonIntroLine2: {
    width: '35%',
    height: 12,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonChevron: {
    width: 16,
    height: 16,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonStepIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#E7E6E0',
  },
  skeletonStepLine1: {
    width: '70%',
    height: 14,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonStepLine2: {
    width: '40%',
    height: 12,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
});
